/**
 * Zentrales Frontend-Logging-System
 * Kommuniziert mit dem Backend-Logging-System
 */

import { getAuthHeaders, getCurrentUser } from './authUtils';
import { getApiBaseUrl } from './apiConfig';

class FrontendLogger {
    constructor(processName) {
        this.processName = processName;
    }
    
    get apiUrl() {
        return getApiBaseUrl();
    }

    /**
     * Log-Eintrag an das Backend senden
     */
    async _sendToBackend(level, message, additionalData = null) {
        try {
            const authHeaders = getAuthHeaders();
            if (!authHeaders['X-Session-ID']) {
                console.warn('No authentication token available for logging');
                return;
            }

            const response = await fetch(`${this.apiUrl}/admin/logs`, {
                method: 'POST',
                headers: {
                    ...authHeaders,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    process: this.processName,
                    level: level,
                    message: message,
                    additional_data: additionalData
                })
            });

            if (!response.ok) {
                console.error('Failed to send log to backend:', response.status);
            }
        } catch (error) {
            console.error('Error sending log to backend:', error);
        }
    }

    /**
     * DEBUG-Level Log
     */
    debug(message, additionalData = null) {
        console.debug(`[${this.processName}] ${message}`, additionalData);
        this._sendToBackend('DEBUG', message, additionalData);
    }

    /**
     * INFO-Level Log
     */
    info(message, additionalData = null) {
        console.info(`[${this.processName}] ${message}`, additionalData);
        this._sendToBackend('INFO', message, additionalData);
    }

    /**
     * WARN-Level Log
     */
    warn(message, additionalData = null) {
        console.warn(`[${this.processName}] ${message}`, additionalData);
        this._sendToBackend('WARN', message, additionalData);
    }

    /**
     * ERROR-Level Log
     */
    error(message, additionalData = null) {
        console.error(`[${this.processName}] ${message}`, additionalData);
        this._sendToBackend('ERROR', message, additionalData);
    }

    /**
     * CRITICAL-Level Log
     */
    critical(message, additionalData = null) {
        console.error(`[${this.processName}] CRITICAL: ${message}`, additionalData);
        this._sendToBackend('CRITICAL', message, additionalData);
    }

    /**
     * Benutzer-Aktion loggen
     */
    logUserAction(action, details = null, additionalData = null) {
        const data = {
            action: action,
            details: details,
            user_id: this._getCurrentUserId(),
            ...additionalData
        };

        this.info(`User action: ${action}`, data);
    }

    /**
     * API-Request loggen
     */
    logApiRequest(method, url, status, duration = null, additionalData = null) {
        const data = {
            method: method,
            url: url,
            status: status,
            duration_ms: duration,
            user_id: this._getCurrentUserId(),
            ...additionalData
        };

        const level = status < 400 ? 'INFO' : status < 500 ? 'WARN' : 'ERROR';
        const message = `${method} ${url} - ${status}`;
        
        this[level.toLowerCase()](message, data);
    }

    /**
     * Komponenten-Lebenszyklus loggen
     */
    logComponentLifecycle(componentName, lifecycle, additionalData = null) {
        const data = {
            component: componentName,
            lifecycle: lifecycle,
            user_id: this._getCurrentUserId(),
            ...additionalData
        };

        this.debug(`Component ${componentName} ${lifecycle}`, data);
    }

    /**
     * Fehler loggen
     */
    logError(error, context = null, additionalData = null) {
        const data = {
            error_name: error.name,
            error_message: error.message,
            error_stack: error.stack,
            context: context,
            user_id: this._getCurrentUserId(),
            ...additionalData
        };

        this.error(`Error: ${error.message}`, data);
    }

    /**
     * Aktuelle Benutzer-ID ermitteln
     */
    _getCurrentUserId() {
        try {
            const user = getCurrentUser();
            return user?.id || null;
        } catch (error) {
            console.warn('Could not extract user ID from session');
        }
        return null;
    }
}

// Factory-Funktion für Logger-Instanzen
export function getLogger(processName) {
    return new FrontendLogger(processName);
}

// Vordefinierte Logger für häufige Frontend-Prozesse
export const loginLogger = getLogger('frontend:login');
export const dashboardLogger = getLogger('frontend:dashboard');
export const reportingLogger = getLogger('frontend:reporting');
export const adminLogger = getLogger('frontend:admin');
export const userManagementLogger = getLogger('frontend:userManagement');
export const serverManagementLogger = getLogger('frontend:serverManagement');
export const statisticsLogger = getLogger('frontend:statistics');

// Globaler Logger für allgemeine Frontend-Ereignisse
export const frontendLogger = getLogger('frontend:main');

// Error Boundary Logger
export const errorBoundaryLogger = getLogger('frontend:errorBoundary');

// API Logger
export const apiLogger = getLogger('frontend:api');

export default FrontendLogger; 